/**
 * BankDashboard — Phase A operating console for the ecosystem ledger.
 *
 * Roles:
 *   * Super-admin sees every tenant + every currency + can transfer / deposit / withdraw
 *   * Tenant admin sees their tenant's accounts only
 *   * (Phase B onwards: regular users see only their own wallets)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Building2, Coins, Wallet, ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine,
  RotateCcw, Plus, Loader2, ArrowLeft, Settings, ShieldCheck, Clock, Check, X,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const fmtMoney = (n, sym = '') => {
  const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return sym ? `${sym} ${v}` : v;
};

export default function BankDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [createOpen, setCreateOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [statementFor, setStatementFor] = useState(null);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  const { data: dashboard } = useQuery({ queryKey: ['bank-dash'], queryFn: () => api('/api/bank/dashboard') });
  const { data: meta } = useQuery({ queryKey: ['bank-meta'], queryFn: () => api('/api/bank/meta') });
  const { data: currencies = [] } = useQuery({ queryKey: ['bank-currencies'], queryFn: () => api('/api/bank/currencies') });
  const { data: accounts = [] } = useQuery({ queryKey: ['bank-accounts'], queryFn: () => api('/api/bank/accounts?limit=200') });
  const { data: pendingData } = useQuery({
    queryKey: ['bank-pending'],
    queryFn: () => api('/api/bank/transfers/pending'),
    refetchInterval: 30000,
  });
  const pendingTransfers = pendingData?.transfers || [];
  const pendingCount = pendingTransfers.length;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bank-dash'] });
    qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    qc.invalidateQueries({ queryKey: ['bank-pending'] });
  };

  const symFor = (code) => (currencies.find((c) => c.code === code) || {}).symbol || code;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bank-dashboard">
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="bank-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold flex items-center gap-2"><Building2 className="h-5 w-5 text-emerald-600" /> Ecosystem Bank</h1>
            <p className="text-xs text-slate-500">Phase A · Multi-currency double-entry ledger</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCurrencyOpen(true)} data-testid="bank-currencies-btn"><Coins className="h-4 w-4 mr-1" /> Currencies</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="bank-create-account-btn"><Plus className="h-4 w-4 mr-1" /> New account</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 sm:p-4 space-y-4">
        {/* Currency totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid="bank-currency-totals">
          {Object.entries(dashboard?.currencies || {}).map(([code, info]) => (
            <Card key={code} className="border-emerald-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase font-semibold text-slate-500">{code}</div>
                <div className="text-2xl font-bold text-emerald-700">{fmtMoney(info.total, symFor(code))}</div>
                <div className="text-xs text-slate-500 mt-1">{info.accounts} accounts</div>
              </CardContent>
            </Card>
          ))}
          {Object.keys(dashboard?.currencies || {}).length === 0 && (
            <Card><CardContent className="p-4 text-sm text-slate-500">No funded accounts yet — start by creating one.</CardContent></Card>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setTransferOpen(true)} data-testid="bank-transfer-btn"><ArrowLeftRight className="h-4 w-4 mr-1" /> Transfer</Button>
          <Button variant="outline" onClick={() => setDepositOpen(true)} data-testid="bank-deposit-btn"><ArrowDownToLine className="h-4 w-4 mr-1" /> Deposit</Button>
          <Button variant="outline" onClick={() => setDepositOpen('withdraw')} data-testid="bank-withdraw-btn"><ArrowUpFromLine className="h-4 w-4 mr-1" /> Withdraw</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="bank-tab-overview">Recent activity</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="bank-tab-accounts">Accounts ({accounts.length})</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="bank-tab-approvals" className="gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Approvals
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]" data-testid="bank-pending-badge">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Recent transactions</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-0 sm:min-w-[520px]">
                  <thead className="bg-slate-100"><tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Amount</th><th className="p-2 text-left">Source</th><th className="p-2 text-left">Memo</th><th className="p-2 text-left">Status</th></tr></thead>
                  <tbody>
                    {(dashboard?.recent_transactions || []).length === 0 ? (
                      <tr><td colSpan={6} className="p-4 text-center text-slate-400">No transactions yet.</td></tr>
                    ) : dashboard.recent_transactions.map((t) => (
                      <tr key={t.id} className="border-t hover:bg-slate-50" data-testid={`bank-txn-${t.id}`}>
                        <td className="p-2 text-xs">{(t.created_at || '').replace('T', ' ').slice(0, 16)}</td>
                        <td className="p-2"><Badge variant="outline" className="text-[10px] uppercase">{t.transaction_type}</Badge></td>
                        <td className="p-2 font-mono">{fmtMoney(t.amount, symFor(t.currency))}</td>
                        <td className="p-2 text-xs text-slate-500">{t.source_module}{t.source_ref_id ? ` · ${t.source_ref_id.slice(-8)}` : ''}</td>
                        <td className="p-2 text-xs">{t.memo || '—'}</td>
                        <td className="p-2"><Badge variant={t.status === 'reversed' ? 'destructive' : 'outline'} className="text-[10px]">{t.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="accounts">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-0 sm:min-w-[520px]">
                  <thead className="bg-slate-100"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Owner</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Currency</th><th className="p-2 text-right">Balance</th><th className="p-2 text-left">Status</th><th className="p-2 text-right">Actions</th></tr></thead>
                  <tbody>
                    {accounts.length === 0 ? (
                      <tr><td colSpan={7} className="p-4 text-center text-slate-400">No accounts yet — click "New account".</td></tr>
                    ) : accounts.map((a) => (
                      <tr key={a.id} className="border-t hover:bg-slate-50" data-testid={`bank-account-row-${a.id}`}>
                        <td className="p-2 font-medium">{a.name}</td>
                        <td className="p-2 text-xs text-slate-500">{a.owner_type}:{(a.owner_id || '').slice(-6)}</td>
                        <td className="p-2"><Badge variant="outline" className="text-[10px]">{a.account_type}</Badge></td>
                        <td className="p-2">{a.currency}</td>
                        <td className="p-2 text-right font-mono">{fmtMoney(a.balance, symFor(a.currency))}</td>
                        <td className="p-2"><Badge variant={a.status === 'active' ? 'outline' : 'destructive'} className="text-[10px]">{a.status}</Badge></td>
                        <td className="p-2 text-right">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setStatementFor(a)} data-testid={`bank-account-statement-${a.id}`}><Wallet className="h-3.5 w-3.5" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approvals">
            <ApprovalsPanel
              pending={pendingTransfers}
              meta={meta}
              symFor={symFor}
              onChanged={refresh}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Create account */}
      <CreateAccountDialog open={createOpen} onClose={() => setCreateOpen(false)} meta={meta} currencies={currencies} onCreated={refresh} />

      {/* Transfer */}
      <TransferDialog open={transferOpen} onClose={() => setTransferOpen(false)} accounts={accounts} meta={meta} symFor={symFor} onDone={refresh} />

      {/* Deposit / Withdraw */}
      <DepositWithdrawDialog mode={depositOpen} onClose={() => setDepositOpen(false)} accounts={accounts} onDone={refresh} />

      {/* Statement */}
      {statementFor && <StatementDialog account={statementFor} onClose={() => setStatementFor(null)} symFor={symFor} />}

      {/* Currency manager */}
      <CurrencyDialog open={currencyOpen} onClose={() => setCurrencyOpen(false)} currencies={currencies} onChange={() => qc.invalidateQueries({ queryKey: ['bank-currencies'] })} />
    </div>
  );
}


function CreateAccountDialog({ open, onClose, meta, currencies, onCreated }) {
  const [form, setForm] = useState({
    owner_type: 'institution', owner_id: '', account_type: 'operating',
    currency: 'ZIG', tenant_id: '', name: '',
  });
  const m = useMutation({
    mutationFn: () => api('/api/bank/accounts', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Account created'); onCreated(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-create-account-dialog">
        <DialogHeader><DialogTitle>New account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Owner type</Label>
              <Select value={form.owner_type} onValueChange={(v) => setForm({ ...form, owner_type: v })}>
                <SelectTrigger data-testid="bank-create-owner-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.owner_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account type</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger data-testid="bank-create-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.account_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Owner ID</Label><Input value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} placeholder="tenant id / user id / fund slug" data-testid="bank-create-owner-id" /></div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="bank-create-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Display name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. FWBC fee collection" data-testid="bank-create-name" /></div>
          <div><Label>Tenant ID (optional)</Label><Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} placeholder="link to a school/church/event tenant" data-testid="bank-create-tenant-id" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.owner_id || m.isPending} onClick={() => m.mutate()} data-testid="bank-create-account-save">
            {m.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TransferDialog({ open, onClose, accounts, meta, symFor, onDone }) {
  const [form, setForm] = useState({ from_account_id: '', to_account_id: '', amount: '', currency: 'ZIG', memo: '' });
  const m = useMutation({
    mutationFn: () => api('/api/bank/transfers/request', { method: 'POST', body: JSON.stringify({ ...form, amount: Number(form.amount) }) }),
    onSuccess: (res) => {
      if (res?.pending) {
        toast.success('Awaiting approval', { description: res.message || 'Transfer queued for second-person approval.' });
      } else {
        toast.success('Transfer posted');
      }
      onDone();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  const fromAcc = accounts.find((a) => a.id === form.from_account_id);
  const threshold = meta?.approval_thresholds?.[form.currency];
  const willQueue = threshold != null && Number(form.amount) >= threshold;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-transfer-dialog">
        <DialogHeader><DialogTitle>Transfer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>From</Label>
            <Select value={form.from_account_id} onValueChange={(v) => setForm({ ...form, from_account_id: v, currency: accounts.find((a) => a.id === v)?.currency || 'ZIG' })}>
              <SelectTrigger data-testid="bank-transfer-from"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency} {a.balance.toFixed(2)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>To</Label>
            <Select value={form.to_account_id} onValueChange={(v) => setForm({ ...form, to_account_id: v })}>
              <SelectTrigger data-testid="bank-transfer-to"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.id !== form.from_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bank-transfer-amount" /></div>
            <div><Label>Memo</Label><Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} data-testid="bank-transfer-memo" /></div>
          </div>
          {threshold != null && (
            <p className="text-xs text-slate-500" data-testid="bank-transfer-threshold-hint">
              Approval threshold for {form.currency}: <span className="font-mono">{symFor ? symFor(form.currency) : ''} {Number(threshold).toLocaleString()}</span>
              {' '} — transfers at or above queue for second-person sign-off.
            </p>
          )}
          {willQueue && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="bank-transfer-will-queue">
              <ShieldCheck className="h-3.5 w-3.5 inline mr-1" />
              This transfer will be <b>queued for approval</b> — a second authorised admin must approve it before funds move.
            </div>
          )}
          {fromAcc && Number(form.amount) > fromAcc.balance && (
            <p className="text-xs text-amber-600">⚠ Amount exceeds source balance — transfer will go through (overdraft) but flag for review.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.from_account_id || !form.to_account_id || !Number(form.amount) || m.isPending} onClick={() => m.mutate()} data-testid="bank-transfer-confirm">
            {m.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{willQueue ? 'Submit for approval' : 'Post transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DepositWithdrawDialog({ mode, onClose, accounts, onDone }) {
  const isWithdraw = mode === 'withdraw';
  const [form, setForm] = useState({ account_id: '', amount: '', currency: 'ZIG', memo: '', external_provider: '', external_ref: '', payee_name: '', payee_phone: '', send_receipt: false });
  const m = useMutation({
    mutationFn: () => api(`/api/bank/${isWithdraw ? 'withdraw' : 'deposit'}`, {
      method: 'POST', body: JSON.stringify({ ...form, amount: Number(form.amount) }),
    }),
    onSuccess: (res) => {
      const wa = res?.whatsapp;
      if (form.send_receipt && wa) {
        if (wa.status === 'sent' || wa.status === 'queued') {
          toast.success(`${isWithdraw ? 'Withdrawal' : 'Deposit'} posted · WhatsApp receipt sent`);
        } else {
          toast.success(`${isWithdraw ? 'Withdrawal' : 'Deposit'} posted · WhatsApp receipt: ${wa.status}${wa.error ? ` (${String(wa.error).slice(0, 60)})` : ''}`);
        }
      } else {
        toast.success(`${isWithdraw ? 'Withdrawal' : 'Deposit'} posted`);
      }
      onDone(); onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={isWithdraw ? "bank-withdraw-dialog" : "bank-deposit-dialog"}>
        <DialogHeader><DialogTitle>{isWithdraw ? 'Withdraw' : 'Deposit'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Account</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v, currency: accounts.find((a) => a.id === v)?.currency || 'ZIG' })}>
              <SelectTrigger data-testid="bank-dw-account"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bank-dw-amount" /></div>
            <div><Label>Memo</Label><Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} data-testid="bank-dw-memo" /></div>
          </div>
          {!isWithdraw && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Provider</Label><Input value={form.external_provider} onChange={(e) => setForm({ ...form, external_provider: e.target.value })} placeholder="stripe / paystack / cash" /></div>
              <div><Label>External ref</Label><Input value={form.external_ref} onChange={(e) => setForm({ ...form, external_ref: e.target.value })} placeholder="pi_xxx / txn id" /></div>
            </div>
          )}

          {/* WhatsApp Receipt — iter 309 */}
          <div className="border-t pt-3 mt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={!!form.send_receipt}
                onChange={(e) => setForm({ ...form, send_receipt: e.target.checked })}
                data-testid="bank-dw-send-receipt"
              />
              <span>Send WhatsApp receipt to payee</span>
            </label>
            {form.send_receipt && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Payee name</Label>
                  <Input value={form.payee_name} onChange={(e) => setForm({ ...form, payee_name: e.target.value })} placeholder="John Moyo" data-testid="bank-dw-payee-name" />
                </div>
                <div>
                  <Label className="text-xs">Payee phone (WhatsApp)</Label>
                  <Input value={form.payee_phone} onChange={(e) => setForm({ ...form, payee_phone: e.target.value })} placeholder="+263 77 000 0000" data-testid="bank-dw-payee-phone" />
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.account_id || !Number(form.amount) || (form.send_receipt && !form.payee_phone.trim()) || m.isPending} onClick={() => m.mutate()} data-testid="bank-dw-confirm">
            {m.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{isWithdraw ? 'Withdraw' : 'Deposit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatementDialog({ account, onClose, symFor }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['bank-statement', account.id],
    queryFn: () => api(`/api/bank/accounts/${account.id}/statement?limit=200`),
  });
  const reverseMutation = useMutation({
    mutationFn: (txnId) => api(`/api/bank/transactions/${txnId}/reverse`, { method: 'POST' }),
    onSuccess: () => { toast.success('Transaction reversed'); qc.invalidateQueries({ queryKey: ['bank-statement', account.id] }); qc.invalidateQueries({ queryKey: ['bank-dash'] }); qc.invalidateQueries({ queryKey: ['bank-accounts'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bank-statement-dialog">
        <DialogHeader><DialogTitle>Statement · {account.name}</DialogTitle></DialogHeader>
        <div className="text-sm">
          <div className="mb-2 flex items-center gap-3">
            <Badge variant="outline">{account.currency}</Badge>
            <span className="font-bold text-emerald-700">{symFor(account.currency)} {Number(account.balance).toFixed(2)}</span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-0 sm:min-w-[520px]">
              <thead className="bg-slate-100 sticky top-0"><tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Memo</th><th className="p-2 text-left">Source</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th><th className="p-2 text-right">Balance</th><th className="p-2"></th></tr></thead>
              <tbody>
                {(data?.entries || []).length === 0 ? (
                  <tr><td colSpan={7} className="p-4 text-center text-slate-400">No activity.</td></tr>
                ) : data.entries.map((e) => (
                  <tr key={e.id} className="border-t" data-testid={`bank-entry-${e.id}`}>
                    <td className="p-2 text-xs">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="p-2 text-xs">{e.memo || '—'}</td>
                    <td className="p-2 text-xs text-slate-500">{e.source_module}</td>
                    <td className="p-2 text-right font-mono text-rose-600">{e.direction === 'debit' ? Number(e.amount).toFixed(2) : ''}</td>
                    <td className="p-2 text-right font-mono text-emerald-600">{e.direction === 'credit' ? Number(e.amount).toFixed(2) : ''}</td>
                    <td className="p-2 text-right font-mono">{e.balance_after != null ? Number(e.balance_after).toFixed(2) : '—'}</td>
                    <td className="p-2 text-right"><Button variant="ghost" size="icon" className="h-7 w-7" title="Reverse" onClick={() => { if (window.confirm('Reverse this transaction?')) reverseMutation.mutate(e.transaction_id); }} data-testid={`bank-reverse-${e.transaction_id}`}><RotateCcw className="h-3 w-3" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CurrencyDialog({ open, onClose, currencies, onChange }) {
  const [form, setForm] = useState({ code: '', name: '', symbol: '', decimals: 2, fx_rate_to_base: 1.0, is_token: false });
  const upsert = useMutation({
    mutationFn: () => api('/api/bank/currencies', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Currency saved'); onChange(); setForm({ code: '', name: '', symbol: '', decimals: 2, fx_rate_to_base: 1.0, is_token: false }); },
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (code) => api(`/api/bank/currencies/${code}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Currency removed'); onChange(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-currencies-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Coins className="h-4 w-4" /> Currencies</DialogTitle></DialogHeader>
        <div className="overflow-x-auto"><table className="w-full text-sm mb-4 min-w-0 sm:min-w-[520px]">
          <thead className="bg-slate-100"><tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">FX → base</th><th className="p-2 text-left">Type</th><th></th></tr></thead>
          <tbody>
            {currencies.map((c) => (
              <tr key={c.code} className="border-t">
                <td className="p-2 font-mono">{c.code}{c.is_base && <Badge variant="outline" className="ml-2 text-[9px]">BASE</Badge>}</td>
                <td className="p-2">{c.name} <span className="text-slate-400">{c.symbol}</span></td>
                <td className="p-2 text-right font-mono">{Number(c.fx_rate_to_base).toFixed(4)}</td>
                <td className="p-2">{c.is_token ? <Badge variant="outline">token</Badge> : 'fiat'}</td>
                <td className="p-2 text-right">{!c.is_base && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm(`Remove ${c.code}?`)) remove.mutate(c.code); }}><RotateCcw className="h-3 w-3" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <p className="text-xs font-semibold text-slate-600 mb-2">Add or update a currency</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input placeholder="Code (e.g. NGN)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="bank-cur-code" />
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bank-cur-name" />
          <Input placeholder="Symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} data-testid="bank-cur-symbol" />
          <Input type="number" step="0.0001" placeholder="FX → base" value={form.fx_rate_to_base} onChange={(e) => setForm({ ...form, fx_rate_to_base: Number(e.target.value) })} data-testid="bank-cur-fx" />
          <Input type="number" placeholder="Decimals" value={form.decimals} onChange={(e) => setForm({ ...form, decimals: Number(e.target.value) })} data-testid="bank-cur-decimals" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_token} onChange={(e) => setForm({ ...form, is_token: e.target.checked })} /> Token / FaithCoin-ready</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button disabled={!form.code || !form.name || upsert.isPending} onClick={() => upsert.mutate()} data-testid="bank-cur-save">
            {upsert.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save currency
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ApprovalsPanel({ pending, meta, symFor, onChanged }) {
  const myId = meta?.me?.id || null;
  const myEmail = meta?.me?.email || null;
  const [rejectFor, setRejectFor] = useState(null);

  const approveMut = useMutation({
    mutationFn: (id) => api(`/api/bank/transfers/${id}/approve`, { method: 'POST', body: JSON.stringify({ notes: '' }) }),
    onSuccess: () => { toast.success('Transfer approved & posted'); onChanged(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="bank-approvals-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          Pending approvals
          <Badge variant="outline" className="ml-1 text-[10px]" data-testid="bank-approvals-count">{pending.length}</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          High-value transfers require a separate authorised admin to approve. Initiators cannot approve their own requests.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-0 sm:min-w-[520px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-2 text-left">Initiated</th>
              <th className="p-2 text-left">From</th>
              <th className="p-2 text-left">To</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-left">Initiator</th>
              <th className="p-2 text-left">Memo</th>
              <th className="p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-slate-400" data-testid="bank-approvals-empty">
                  <Check className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
                  Nothing waiting — every transfer is below the approval threshold or already cleared.
                </td>
              </tr>
            ) : pending.map((p) => {
              const isInitiator = !!myId && p.initiated_by_id === myId;
              return (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`bank-pending-row-${p.id}`}>
                  <td className="p-2 text-xs whitespace-nowrap">
                    <Clock className="h-3 w-3 inline mr-1 text-amber-500" />
                    {(p.initiated_at || '').replace('T', ' ').slice(0, 16)}
                  </td>
                  <td className="p-2 text-xs">
                    <div className="font-medium">{p.from_account_name || '—'}</div>
                    <div className="text-slate-400">{(p.from_account_id || '').slice(-8)}</div>
                  </td>
                  <td className="p-2 text-xs">
                    <div className="font-medium">{p.to_account_name || '—'}</div>
                    <div className="text-slate-400">{(p.to_account_id || '').slice(-8)}</div>
                  </td>
                  <td className="p-2 text-right font-mono font-semibold text-emerald-700">
                    {(symFor ? symFor(p.currency) : '')} {Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <div className="text-[10px] uppercase text-slate-400 font-normal">{p.currency}</div>
                  </td>
                  <td className="p-2 text-xs">
                    {p.initiated_by_email || '—'}
                    {isInitiator && <Badge variant="outline" className="ml-1 text-[9px]" data-testid={`bank-pending-you-${p.id}`}>YOU</Badge>}
                  </td>
                  <td className="p-2 text-xs max-w-[180px] truncate" title={p.memo}>{p.memo || '—'}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      className="h-7 px-2 mr-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={isInitiator || approveMut.isPending}
                      onClick={() => {
                        if (window.confirm(`Approve transfer of ${p.currency} ${Number(p.amount).toLocaleString()}?\n\nThis posts the funds immediately.`)) {
                          approveMut.mutate(p.id);
                        }
                      }}
                      title={isInitiator ? 'You initiated this transfer — a different admin must approve.' : 'Approve & post'}
                      data-testid={`bank-pending-approve-${p.id}`}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" />Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 px-2"
                      onClick={() => setRejectFor(p)}
                      data-testid={`bank-pending-reject-${p.id}`}
                    >
                      <X className="h-3.5 w-3.5 mr-1" />Reject
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </CardContent>
      {rejectFor && (
        <RejectDialog
          pending={rejectFor}
          symFor={symFor}
          onClose={() => setRejectFor(null)}
          onDone={() => { setRejectFor(null); onChanged(); }}
        />
      )}
    </Card>
  );
}


function RejectDialog({ pending, symFor, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api(`/api/bank/transfers/${pending.id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { toast.success('Transfer rejected'); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-reject-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><X className="h-4 w-4 text-rose-600" /> Reject transfer</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-slate-50 border p-2">
            <div className="text-xs text-slate-500">Amount</div>
            <div className="font-mono font-semibold text-rose-700">
              {(symFor ? symFor(pending.currency) : '')} {Number(pending.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {pending.currency}
            </div>
            <div className="text-xs text-slate-500 mt-1">Initiated by {pending.initiated_by_email}</div>
          </div>
          <div>
            <Label>Reason for rejection <span className="text-rose-500">*</span></Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly explain why this transfer is being rejected. The initiator will see this."
              rows={3}
              data-testid="bank-reject-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!reason.trim() || m.isPending}
            onClick={() => m.mutate()}
            data-testid="bank-reject-confirm"
          >
            {m.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
            Reject transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
